import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FaUserCircle, FaSyncAlt, FaHistory } from "react-icons/fa";
import { FiSettings } from "react-icons/fi";
import { HiEye } from "react-icons/hi";
import LeftPanelTopBar from "../LeftPanelTopBar";
import EditTicketModal from "./EditTicketModal";
import { TicketContext } from "../../../../app/providers/TicketContext";
import { TicketWebSocketContext } from "../../../websocket/TicketWebSocketProvider";
import FormView from "./FormView";
import "./LeftPanelTickets.css";

function LeftPanelTickets() {
  const {
    tickets,
    selectTicket,
    eventCache,
    allFields,
    selectedTicket,
    selectedTicketId,
    setSelectedTicket,
    setSelectedTicketId,
    memberList,
    fetchTickets,
    loading: ticketsLoading,
  } = useContext(TicketContext);
  const {
    notificationCounters: wsNotificationCounters,
    setNotificationCounters: setWsNotificationCounters,
    ticketsToMoveToTop,
  } = useContext(TicketWebSocketContext);

  const [detailTicket, setDetailTicket] = useState(null);
  const [isDetailView, setIsDetailView] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ticketToEdit, setTicketToEdit] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [validImages, setValidImages] = useState({});
  const [searchFilter, setSearchFilter] = useState(() => () => true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMainflows, setSelectedMainflows] = useState([]);
  const [visitedTickets, setVisitedTickets] = useState(new Set());
  const ticketListRef = useRef(null);
  const [expandedMainflows, setExpandedMainflows] = useState(new Set());
  const [expandedFlows, setExpandedFlows] = useState(new Set());
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const previousSelectedTicketIdRef = useRef(null); // DetailView'ın otomatik açılmasını kontrol etmek için

  const SESSION_STORAGE_KEY = 'jubi_visited_tickets';
  const LAST_OPENED_TICKET_KEY = 'jubi_last_opened_ticket';
  const [lastOpenedTicketId, setLastOpenedTicketId] = useState(null);

  // sessionStorage'dan ziyaret edilen ticket'ları yükle
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const visitedIds = JSON.parse(stored);
        setVisitedTickets(new Set(visitedIds));
      }
    } catch (error) {
      console.warn('Ziyaret edilen ticket\'lar yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(LAST_OPENED_TICKET_KEY);
      if (stored) {
        setLastOpenedTicketId(stored);
      }
    } catch (error) {
      console.warn('Son açılan ticket yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(LAST_OPENED_TICKET_KEY);
      if (stored) {
        setLastOpenedTicketId(stored);
      }
    } catch (error) {
      console.warn('Son açılan ticket yüklenemedi:', error);
    }
  }, []);

  // Ziyaret edilen ticket'ı işaretle
  const markTicketAsVisited = useCallback((ticketId) => {
    if (!ticketId) return;

    setVisitedTickets((prev) => {
      const newSet = new Set(prev);
      newSet.add(String(ticketId));
      
      // sessionStorage'a kaydet
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(Array.from(newSet)));
      } catch (error) {
        console.warn('Ziyaret edilen ticket kaydedilemedi:', error);
      }
      
      return newSet;
    });
  }, []);

  // Ticket'ın ziyaret edilip edilmediğini kontrol et
  const isTicketVisited = useCallback((ticketId) => {
    if (!ticketId) return false;
    return visitedTickets.has(String(ticketId));
  }, [visitedTickets]);

  const translations = {
    'tickets.myProcess': 'My Flows',
    'tickets.noDescription': 'Açıklama yok',
    'tickets.noTickets': 'Gösterilecek bilet yok',
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      // Status filtresini kontrol et
      const statusMatch =
        activeFilter === 'all' ||
        ticket.STATUS === activeFilter ||
        (typeof ticket.STATUS === 'object' && ticket.STATUS?.value === activeFilter);
      
      // Arama ve mainflow filtrelerini kontrol et
      const searchMatch = searchFilter(ticket);
      
      return statusMatch && searchMatch;
    });
  }, [tickets, searchFilter, activeFilter]);

  // Ticket'ları MAINFLOW ve FLOW'a göre kategorize et
  const categorizedTickets = useMemo(() => {
    const categories = {};
    
    filteredTickets.forEach((ticket) => {
      // MAINFLOW bilgisini al
      let mainflowKey = 'N/A';
      if (ticket?.MAINFLOW_TEXT) {
        mainflowKey = ticket.MAINFLOW_TEXT;
      } else if (ticket?.MAINFLOW) {
        if (typeof ticket.MAINFLOW === 'string') {
          mainflowKey = ticket.MAINFLOW;
        } else if (typeof ticket.MAINFLOW === 'object' && ticket.MAINFLOW?.value) {
          mainflowKey = ticket.MAINFLOW.value;
        }
      }
      
      // FLOW bilgisini al
      let flowKey = 'N/A';
      if (ticket?.FLOWID) {
        flowKey = ticket.FLOWID;
      } else if (ticket?.FLOW) {
        if (typeof ticket.FLOW === 'string') {
          flowKey = ticket.FLOW;
        } else if (typeof ticket.FLOW === 'object' && ticket.FLOW?.value) {
          flowKey = ticket.FLOW.value;
        }
      }
      
      // Kategori yapısını oluştur
      if (!categories[mainflowKey]) {
        categories[mainflowKey] = {};
      }
      
      if (!categories[mainflowKey][flowKey]) {
        categories[mainflowKey][flowKey] = [];
      }
      
      categories[mainflowKey][flowKey].push(ticket);
    });
    
    return categories;
  }, [filteredTickets]);

  // 🎫 TICKET ID BAZLI FİLTRELEME: allFields'ı seçili ticket için cache'den veya global state'den al
  const currentTicketAllFields = useMemo(() => {
    if (!selectedTicketId) {
      return null;
    }

    // Önce cache'den kontrol et
    const cacheEntry = eventCache[selectedTicketId];
    if (cacheEntry && cacheEntry.allFields) {
      return cacheEntry.allFields;
    }

    // Cache'de yoksa, global allFields'ı kullan (sadece seçili ticket için güncelleniyor)
    return allFields;
  }, [selectedTicketId, eventCache, allFields]);

  const getLastEventDate = (ticketId) => {
    const cacheEntry = eventCache[ticketId];
    if (!cacheEntry || !Array.isArray(cacheEntry.events) || cacheEntry.events.length === 0) {
      return null;
    }

    const sortedEvents = [...cacheEntry.events].sort((a, b) => {
      const aId = parseInt(a.EVEID) || 0;
      const bId = parseInt(b.EVEID) || 0;
      return bId - aId;
    });

    return sortedEvents[0]?.ANDAT || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih yok';

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'Geçersiz tarih';

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const eventDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      if (eventDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
      }

      if (eventDate.getTime() === yesterday.getTime()) {
        return 'dün';
      }

      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return 'Tarih hatası';
    }
  };

  const handleOpenLastTicket = useCallback(() => {
    const storedId = lastOpenedTicketId || sessionStorage.getItem(LAST_OPENED_TICKET_KEY);
    if (!storedId) return;
    const ticket = tickets.find(t => String(t.TICID) === String(storedId));
    if (ticket) {
      handleTicketSelection(ticket);
    }
  }, [lastOpenedTicketId, tickets]);

  const handleRefreshList = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchTickets();
    } catch (e) {
      console.error('Liste yenilenemedi:', e);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchTickets, isRefreshing]);

  const handleSearch = useCallback((filterFn) => {
    if (typeof filterFn === 'function') {
      // Sadece arama ve mainflow filtrelerini uygula, status filtresi filteredTickets içinde uygulanıyor
      setSearchFilter(() => filterFn);
    } else {
      // Filtre yoksa tüm ticket'ları göster (status filtresi filteredTickets içinde uygulanıyor)
      setSearchFilter(() => () => true);
    }
  }, []);

  const handleSearchTermChange = (term) => {
    setSearchTerm(term);
  };

  const handleMainflowsChange = (mainflows) => {
    setSelectedMainflows(mainflows);
  };

  // activeFilter değişikliği filteredTickets useMemo'sunda işleniyor, burada ek işlem gerekmiyor

  useEffect(() => {
    if (ticketsToMoveToTop.size > 0 && ticketListRef.current) {
      const updatedTicketId = Array.from(ticketsToMoveToTop)[0];
      const updatedTicketElement = ticketListRef.current.querySelector(
        `[data-ticket-id="${updatedTicketId}"]`,
      );

      if (updatedTicketElement) {
        updatedTicketElement.classList.add('highlight-update');
        updatedTicketElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });

        setTimeout(() => {
          updatedTicketElement.classList.remove('highlight-update');
        }, 2000);
      }
    }
  }, [ticketsToMoveToTop]);

  useEffect(() => {
    const checks = {};
    tickets.forEach((ticket) => {
      if (ticket?.TICID) {
        checks[ticket.TICID] = false;
      }
    });
    setValidImages(checks);
  }, [tickets]);

  // 🎫 TICKET ID BAZLI FİLTRELEME: DetailView sadece seçili ticket için gösterilmeli
  // NOT: handleReturnToList ile DetailView kapatıldığında bu useEffect tekrar açmamalı
  useEffect(() => {
    // Eğer selectedTicketId değişmediyse ve DetailView zaten kapalıysa, hiçbir şey yapma
    // (kullanıcı geri butonuna basmış olabilir)
    if (previousSelectedTicketIdRef.current === selectedTicketId && !isDetailView) {
      return;
    }
    
    // selectedTicketId değiştiyse ref'i güncelle
    const ticketIdChanged = previousSelectedTicketIdRef.current !== selectedTicketId;
    previousSelectedTicketIdRef.current = selectedTicketId;
    
    // selectedTicketId kontrolü
    if (selectedTicketId) {
      // Eğer selectedTicket varsa ve ID'ler eşleşiyorsa, detailTicket'ı güncelle
      // selectedTicket güncellendiğinde (selectTicket çağrısından sonra) detailTicket'ı da güncelle
      if (selectedTicket && String(selectedTicket.TICID) === String(selectedTicketId)) {
        // selectedTicket güncellendiğinde detailTicket'ı da güncelle
        // İlk tıklamada handleTicketSelection zaten detailTicket'ı set etti,
        // burada sadece selectedTicket güncellendiğinde detailTicket'ı güncelliyoruz
        if (ticketIdChanged || (isDetailView && (!detailTicket || String(detailTicket.TICID) !== String(selectedTicketId)))) {
          setDetailTicket(selectedTicket);
          setIsDetailView(true);
        }
      }
      // Eğer selectedTicket henüz yoksa ama detailTicket zaten set edilmişse (handleTicketSelection'dan),
      // DetailView'ı açık tut - selectedTicket güncellenene kadar bekleyecek
      // Bu durumda hiçbir şey yapma, handleTicketSelection zaten DetailView'ı açmış
    } else {
      // selectedTicketId yoksa DetailView'ı kapat
      setDetailTicket(null);
      setIsDetailView(false);
    }

  }, [selectedTicket, selectedTicketId, isDetailView]);

  // Tarayıcı title'ını detailTicket ile güncelle
  useEffect(() => {
    if (detailTicket) {
      const mainflow = detailTicket.MAINFLOW || detailTicket.TDESC || '';
      const ticid = detailTicket.TICID || '';
      document.title = `${mainflow} — #${ticid}`;
    } else {
      document.title = 'DiagRun';
    }
    return () => {
      document.title = 'DiagRun';
    };
  }, [detailTicket]);

  // Ticket seçimi temizlendiğinde expanded state'leri temizleme
  // Kullanıcı dropdown'ların açık kalmasını istiyor, bu yüzden bu useEffect kaldırıldı
  // useEffect(() => {
  //   if (!selectedTicketId && !selectedTicket) {
  //     setExpandedMainflows(new Set());
  //     setExpandedFlows(new Set());
  //     setSelectedFlowId(null);
  //   }
  // }, [selectedTicketId, selectedTicket]);

  // Ticket değiştiğinde seçili flow'u sıfırla
  useEffect(() => {
    if (detailTicket) {
      setSelectedFlowId(null);
    }
  }, [detailTicket?.TICID]);

  const resetWsNotification = (ticketId, e) => {
    e.stopPropagation();
    setWsNotificationCounters((prev) => {
      const newCounters = { ...prev };
      delete newCounters[ticketId];
      return newCounters;
    });
  };

  const myProcess = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          ticket?.STATUS === 'myProcess' ||
          (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'myProcess'),
      ).length,
    [tickets],
  );

  const onGoingProcess = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          ticket?.STATUS === 'onGoingProcess' ||
          (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'onGoingProcess'),
      ).length,
    [tickets],
  );

  const completedProcess = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          ticket?.STATUS === 'completedProcess' ||
          (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'completedProcess'),
      ).length,
    [tickets],
  );

  const handleTicketSelection = (ticket) => {
    if (!ticket?.TICID) {
      console.warn('⚠️ LeftPanelTickets - Ticket TICID yok:', ticket);
      return;
    }

    // Ticket'ı ziyaret edildi olarak işaretle
    markTicketAsVisited(ticket.TICID);
    setLastOpenedTicketId(String(ticket.TICID));
    try {
      sessionStorage.setItem(LAST_OPENED_TICKET_KEY, String(ticket.TICID));
    } catch (error) {
      console.warn('Son açılan ticket kaydedilemedi:', error);
    }
    setLastOpenedTicketId(String(ticket.TICID));
    try {
      sessionStorage.setItem(LAST_OPENED_TICKET_KEY, String(ticket.TICID));
    } catch (error) {
      console.warn('Son açılan ticket kaydedilemedi:', error);
    }

    // 🎫 TICKET ID BAZLI FİLTRELEME: Seçili ticket ID'sini güncelle
    // Eğer aynı ticket tekrar seçiliyorsa bile state'i güncelle ki useEffect tetiklensin
    const ticketId = ticket.TICID;
    setSelectedTicketId(ticketId);
    
    // İlk tıklamada DetailView'ı hemen aç ve detailTicket'ı set et
    // selectedTicket henüz güncellenmemiş olabilir, bu yüzden ticket objesini direkt kullan
    setDetailTicket(ticket);
    setIsDetailView(true);
    
    // selectTicket çağrıldığında selectedTicket ve allFields güncellenecek
    // DetailView useEffect'inde selectedTicket değiştiğinde detailTicket güncellenecek
    selectTicket(ticketId);

    if (wsNotificationCounters[ticketId] > 0) {
      setWsNotificationCounters((prev) => {
        const newCounters = { ...prev };
        delete newCounters[ticketId];
        return newCounters;
      });
    }
  };

  const handleReturnToList = () => {
    // Sadece DetailView'ı kapat, ticket seçimini koru
    // selectedTicketId ve selectedTicket korunmalı ki RightPanelToolbar ve diğer component'ler
    // seçili ticket bilgisini görebilsin
    setDetailTicket(null);
    setIsDetailView(false);
    // selectedTicketId ve selectedTicket'ı TEMİZLEME - korunmalı!
    // Expanded state'leri temizleme - kullanıcı dropdown'ların açık kalmasını istiyor
    // setExpandedMainflows(new Set());
    // setExpandedFlows(new Set());
    // setSelectedFlowId(null);
  };

  const deriveOwnerName = (ticket) => {
    if (!ticket) return 'N/A';
    if (ticket.UNAME) return ticket.UNAME;

    const listEntry = tickets.find((item) => String(item.TICID) === String(ticket.TICID));
    if (listEntry?.UNAME) return listEntry.UNAME;

    return 'N/A';
  };

  const renderTicketSummary = (ticket) => (
    <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/6 px-4 py-4 text-sm shadow-[0_15px_40px_rgba(8,18,38,0.25)]">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-blue-100/70">Ticket</p>
          <h3 className="text-lg font-semibold text-white">#{ticket.TICID}</h3>
        </div>
        <span className="rounded-full bg-blue-500/15 px-3 py-1 text-xs font-semibold text-blue-200">
          {ticket.STATUS?.value || ticket.STATUS || 'N/A'}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Sahibi</p>
          <p className="text-sm font-medium text-slate-100">
            {deriveOwnerName(ticket)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Akış</p>
          <p className="text-sm font-medium text-slate-100">
            {ticket.MAINFLOW_TEXT || ticket.MAINFLOW || 'N/A'}
          </p>
        </div>
        <div className="md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Açıklama</p>
          <p className="text-xs leading-relaxed text-slate-200">
            {ticket.USEREXP || translations['tickets.noDescription']}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Son Güncelleme</p>
          <p className="text-sm font-medium text-slate-100">
            {formatDate(
              ticket.lastEventDate || ticket.LAST_EVENTDATE || ticket.ANDAT || getLastEventDate(ticket.TICID),
            )}
          </p>
        </div>
      </div>
    </div>
  );

  const renderListView = () => (
    <>
      <header className="flex-shrink-0 border-b border-[#22324b] bg-[#22324b] shadow-sm">
        <LeftPanelTopBar 
          onSearch={handleSearch}
          searchTerm={searchTerm}
          selectedMainflows={selectedMainflows}
          onSearchTermChange={handleSearchTermChange}
          onMainflowsChange={handleMainflowsChange}
        />
      </header>

      <div className="flex-shrink-0 border-b border-[#273754] bg-[#323F53] px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filtreleri */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: 'all', label: 'Tümü', count: tickets.length },
              { key: 'onGoingProcess', label: 'Devam Edenler', count: onGoingProcess },
              { key: 'myProcess', label: 'Bende Bekleyenler', count: myProcess },
              { key: 'completedProcess', label: 'Tamamlananlar', count: completedProcess },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium transition-colors duration-150 ${
                  activeFilter === key
                    ? 'bg-blue-500 text-white'
                    : 'bg-[#2A3D5C] text-slate-200 hover:bg-[#314568]'
                }`}
                onClick={() => setActiveFilter(key)}
              >
                <span>{label}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    activeFilter === key 
                      ? 'bg-white/25 text-white' 
                      : 'bg-[#34476a] text-slate-100'
                  }`}
                >
                  {count}
                </span>
              </button>
            ))}
            <button
              onClick={handleRefreshList}
              disabled={isRefreshing}
              className="p-1.5 rounded-lg text-slate-300 transition-colors duration-150 hover:bg-[#314568] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Listeyi yenile"
            >
              <FaSyncAlt size={11} className={isRefreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleOpenLastTicket}
              disabled={!lastOpenedTicketId}
              className="p-1.5 rounded-lg text-slate-300 transition-colors duration-150 hover:bg-[#314568] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Son açılan ticket'a git"
            >
              <FaHistory size={11} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={ticketListRef}
        className="ticket-list flex-1 overflow-y-auto bg-[#1A283F]"
        style={{ scrollbarWidth: 'thin' }}
      >
        {ticketsLoading ? (
          <div className="flex flex-col gap-0.5 p-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-lg bg-[#22324b]/60 p-3 animate-pulse" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex items-center gap-2.5">
                  <div className="h-7 w-7 rounded-full bg-slate-600/40 flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-2.5 w-3/5 rounded bg-slate-600/40" />
                    <div className="h-2 w-2/5 rounded bg-slate-700/30" />
                  </div>
                  <div className="h-4 w-10 rounded-full bg-slate-600/30" />
                </div>
              </div>
            ))}
          </div>
        ) : Object.keys(categorizedTickets).length > 0 ? (
          <div className="divide-y divide-[#22324b]/50">
            {Object.entries(categorizedTickets)
              .sort(([a], [b]) => a.localeCompare(b, 'tr'))
              .map(([mainflowKey, flows]) => {
                const mainflowId = `mainflow-${mainflowKey}`;
                const isMainflowExpanded = expandedMainflows.has(mainflowKey);
                const mainflowTicketCount = Object.values(flows).reduce((sum, tickets) => sum + tickets.length, 0);

                return (
                  <div key={mainflowKey} className="border-b border-[#22324b]/30">
                    {/* Ana Kategori: MAINFLOW */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedMainflows((prev) => {
                          const newSet = new Set(prev);
                          if (newSet.has(mainflowKey)) {
                            newSet.delete(mainflowKey);
                          } else {
                            newSet.add(mainflowKey);
                          }
                          return newSet;
                        });
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-colors duration-150 text-left border-l-4 ${
                        isMainflowExpanded
                          ? 'bg-blue-600/25 border-blue-400'
                          : 'bg-slate-700/50 border-slate-500 hover:bg-slate-600/60'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <svg
                          className={`h-5 w-5 transition-transform duration-200 ${
                            isMainflowExpanded ? 'rotate-90 text-blue-300' : 'text-slate-300'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className={`font-bold text-[12px] ${
                          isMainflowExpanded ? 'text-blue-100' : 'text-slate-100'
                        }`}>
                          {mainflowKey}
                        </span>
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                          isMainflowExpanded
                            ? 'bg-blue-500/80 text-white shadow-md'
                            : 'bg-slate-600/70 text-slate-200'
                        }`}>
                          {mainflowTicketCount}
                        </span>
                      </div>
                    </button>

                    {/* Alt Kategoriler: FLOW */}
                    {isMainflowExpanded && (
                      <div className="bg-gradient-to-b from-slate-800/40 to-slate-900/30">
                        {Object.entries(flows)
                          .sort(([a], [b]) => a.localeCompare(b, 'tr'))
                          .map(([flowKey, tickets]) => {
                            const flowId = `flow-${mainflowKey}-${flowKey}`;
                            const isFlowExpanded = expandedFlows.has(flowId);

                            return (
                              <div key={flowKey} className="border-l-4 border-emerald-500/60 ml-4 my-1">
                                {/* Alt Kategori Header */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedFlows((prev) => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(flowId)) {
                                        newSet.delete(flowId);
                                      } else {
                                        newSet.add(flowId);
                                      }
                                      return newSet;
                                    });
                                  }}
                                  className={`w-full flex items-center justify-between px-5 py-2.5 transition-colors duration-150 text-left ${
                                    isFlowExpanded
                                      ? 'bg-emerald-600/30'
                                      : 'bg-slate-600/40 hover:bg-slate-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 flex-1">
                                    <svg
                                      className={`h-4 w-4 transition-transform duration-200 ${
                                        isFlowExpanded ? 'rotate-90 text-emerald-300' : 'text-slate-300'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                    </svg>
                                    <span className={`font-semibold text-[11px] ${
                                      isFlowExpanded ? 'text-emerald-100' : 'text-slate-200'
                                    }`}>
                                      {flowKey}
                                    </span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                      isFlowExpanded
                                        ? 'bg-emerald-500/80 text-white shadow-sm'
                                        : 'bg-slate-500/60 text-slate-200'
                                    }`}>
                                      {tickets.length}
                                    </span>
                                  </div>
                                </button>

                                {/* Ticket Listesi */}
                                {isFlowExpanded && (
                                  <ul className="divide-y divide-emerald-400/20 ml-2">
                                    {[...tickets]
                                      .sort((a, b) => {
                                        // Ticketları oluşturulma/oluşma tarihine göre sırala (en yeni üstte)
                                        const getCreatedTs = (t) => {
                                          const raw =
                                            t?.ANDAT ??
                                            t?.created_at ??
                                            t?.CREATED_AT ??
                                            t?.createdAt ??
                                            t?.LAST_EVENTDATE ??
                                            t?.lastEventDate ??
                                            null;
                                          if (!raw) return 0;
                                          const d = new Date(raw);
                                          const ts = d.getTime();
                                          return Number.isNaN(ts) ? 0 : ts;
                                        };
                                        const aTs = getCreatedTs(a);
                                        const bTs = getCreatedTs(b);
                                        if (aTs !== bTs) return bTs - aTs;

                                        // Tie-breaker: TICID büyük olan üstte
                                        const aId = parseInt(a?.TICID, 10) || 0;
                                        const bId = parseInt(b?.TICID, 10) || 0;
                                        return bId - aId;
                                      })
                                      .map((ticket) => (
                                      <li
                                        key={ticket?.TICID || Math.random()}
                                        data-ticket-id={ticket?.TICID}
                                        onClick={() => handleTicketSelection(ticket)}
                                        className={`group relative flex cursor-pointer items-start gap-3 px-5 py-2.5 text-[10.5px] transition-colors duration-150 border-l-4 ${
                                          selectedTicketId === ticket?.TICID
                                            ? 'bg-blue-50 border-blue-500'
                                            : 'bg-white/90 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                                        } ${ticketsToMoveToTop.has(ticket?.TICID) ? 'updated' : ''}`}
                                      >
                                        <div className="flex items-center justify-center rounded-full p-1 transition-colors bg-[#d5def0] text-[#2b4a7a]">
                                          <FaUserCircle className="h-4 w-4" />
                                        </div>

                                          <div className="flex min-w-0 flex-1 items-start gap-3">
                                          <div className="min-w-0 flex-1 space-y-1.5">
                                            <p className="truncate text-[11px] font-semibold text-slate-800">
                                              {(() => {
                                                if (ticket?.UNAME && typeof ticket.UNAME === 'object') {
                                                  try {
                                                    return JSON.stringify(ticket.UNAME);
                                                  } catch (e) {
                                                    return 'N/A';
                                                  }
                                                }
                                                if (ticket?.UNAME && typeof ticket.UNAME === 'string') {
                                                  return ticket.UNAME;
                                                }
                                                return 'N/A';
                                              })()}
                                            </p>
                                            <p className="truncate text-[10.5px] font-medium text-slate-700">
                                              {(() => {
                                                if (ticket?.USEREXP && typeof ticket.USEREXP === 'object') {
                                                  try {
                                                    return JSON.stringify(ticket.USEREXP);
                                                  } catch (e) {
                                                    return translations['tickets.noDescription'];
                                                  }
                                                }
                                                if (ticket?.USEREXP && typeof ticket.USEREXP === 'string') {
                                                  return ticket.USEREXP;
                                                }
                                                return translations['tickets.noDescription'];
                                              })()}
                                            </p>
                                          </div>

                                          <div className="flex flex-col items-end gap-1 text-[9.5px] text-slate-500">
                                            {isTicketVisited(ticket?.TICID) && (
                                              <div
                                                className="flex items-center justify-center rounded-full bg-blue-500/10 p-1 transition-colors group-hover:bg-blue-500/20"
                                                title="Bu ticket daha önce görüntülendi"
                                              >
                                                <HiEye className="h-3 w-3 text-blue-600" />
                                              </div>
                                            )}
                                            <div className="text-right text-[9.5px] text-slate-500">
                                              {(() => {
                                                const createdAt =
                                                  ticket?.ANDAT ||
                                                  ticket?.created_at ||
                                                  ticket?.updated_at ||
                                                  ticket?.LAST_EVENTDATE ||
                                                  getLastEventDate(ticket?.TICID);
                                                if (!createdAt) {
                                                  return <div className="text-[9.5px] text-slate-400">Tarih yok</div>;
                                                }
                                                const dateObj = new Date(createdAt);
                                                if (Number.isNaN(dateObj.getTime())) {
                                                  return <div className="text-[9.5px] text-slate-400">Geçersiz tarih</div>;
                                                }
                                                return (
                                                  <>
                                                    <div className="font-medium text-slate-600">
                                                      {dateObj.toLocaleDateString('tr-TR')}
                                                    </div>
                                                    <div className="text-slate-500">
                                                      {dateObj.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="ml-auto flex items-center gap-2">
                                          <button
                                            className={`rounded-full p-1.5 transition-colors ${
                                              ticket?.STATUS === 'await' ||
                                              (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'await')
                                                ? 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20'
                                                : ticket?.STATUS === 'continue' ||
                                                  (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'continue')
                                                ? 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'
                                                : 'bg-slate-500/10 text-slate-600 hover:bg-slate-500/20'
                                            }`}
                                            title="Bileti düzenle"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setTicketToEdit(ticket);
                                              setIsModalOpen(true);
                                            }}
                                          >
                                            <FiSettings className="h-3.5 w-3.5" />
                                          </button>
                                          {wsNotificationCounters[ticket?.TICID] > 0 && (
                                            <button
                                              className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[9.5px] font-semibold text-white transition-colors hover:bg-orange-600"
                                              onClick={(e) => resetWsNotification(ticket?.TICID, e)}
                                              title="WebSocket bildirimini sıfırla"
                                            >
                                              {wsNotificationCounters[ticket?.TICID]}
                                            </button>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-[11px] text-slate-500">
            {translations['tickets.noTickets']}
          </div>
        )}
      </div>
    </>
  );

  // Flow görselleştirmesi için event'lerden flow'ları çıkar
  const getTicketFlows = useMemo(() => {
    if (!detailTicket?.TICID) return [];

    const cacheEntry = eventCache[detailTicket.TICID];
    if (!cacheEntry || !Array.isArray(cacheEntry.events) || cacheEntry.events.length === 0) {
      return [];
    }

    // Event'leri tarih sırasına göre sırala
    const sortedEvents = [...cacheEntry.events].sort((a, b) => {
      const aDate = new Date(a.ANDAT || 0);
      const bDate = new Date(b.ANDAT || 0);
      return aDate - bDate;
    });

    // Benzersiz flow'ları çıkar (ilk görüldüğü sıraya göre)
    const flowMap = new Map();
    sortedEvents.forEach((event) => {
      const flowId = event.FLOWID || event.FLOW || null;
      if (flowId && !flowMap.has(flowId)) {
        flowMap.set(flowId, {
          flowId,
          firstSeenDate: event.ANDAT,
          eventCount: 1,
        });
      } else if (flowId) {
        flowMap.get(flowId).eventCount += 1;
      }
    });

    return Array.from(flowMap.values());
  }, [detailTicket, eventCache]);

  // Mevcut flow'u belirle
  const getCurrentFlow = useMemo(() => {
    if (!detailTicket) return null;
    return detailTicket.FLOWID || detailTicket.FLOW || null;
  }, [detailTicket]);

  // İlk flow'u otomatik seç (detailTicket veya flow'lar değiştiğinde)
  useEffect(() => {
    if (getTicketFlows.length > 0) {
      // Eğer seçili flow yoksa veya mevcut flow'lar arasında yoksa ilk flow'u seç
      if (!selectedFlowId || !getTicketFlows.find(flow => flow.flowId === selectedFlowId)) {
        setSelectedFlowId(getTicketFlows[0].flowId);
      }
    } else {
      // Flow yoksa seçimi temizle
      setSelectedFlowId(null);
    }
  }, [detailTicket?.TICID, getTicketFlows]);

  // Seçili flow'un event'lerini al
  const getSelectedFlowEvents = useMemo(() => {
    if (!selectedFlowId || !detailTicket?.TICID) return null;
    
    const cacheEntry = eventCache[detailTicket.TICID];
    if (!cacheEntry || !Array.isArray(cacheEntry.events)) return null;

    const flowEvents = cacheEntry.events.filter(event => 
      (event.FLOWID || event.FLOW) === selectedFlowId
    );


    return flowEvents;
  }, [selectedFlowId, detailTicket, eventCache]);

  // Seçili flow'un allFields'ını al - allFields içinde flow ID'si section key olarak kullanılıyor
  const getSelectedFlowAllFields = useMemo(() => {
    if (!selectedFlowId || !detailTicket?.TICID) return null;
    
    // Cache'deki allFields'ı al
    const cacheEntry = eventCache[detailTicket.TICID];
    const baseAllFields = cacheEntry?.allFields || currentTicketAllFields;
    
    if (!baseAllFields || typeof baseAllFields !== 'object') return null;

    // allFields içinde seçili flow'un section'ını bul
    // Flow ID'si section key olarak kullanılıyor (talepOlustur, teklifTopla, vb.)
    if (baseAllFields[selectedFlowId]) {
      return {
        [selectedFlowId]: baseAllFields[selectedFlowId]
      };
    }

    // Eğer section yoksa null döndür (event listesi gösterilecek)
    return null;
  }, [selectedFlowId, detailTicket, eventCache, currentTicketAllFields]);

  // Seçili flow'un renk şemasını al
  const getSelectedFlowColorScheme = useMemo(() => {
    if (!selectedFlowId) return null;
    
    const flows = getTicketFlows;
    const flowIndex = flows.findIndex(flow => flow.flowId === selectedFlowId);
    
    if (flowIndex === -1) return null;

    const flowColors = [
      { bg: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-400/50', text: 'text-blue-200', dot: 'bg-blue-500' },
      { bg: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-400/50', text: 'text-purple-200', dot: 'bg-purple-500' },
      { bg: 'from-emerald-500/20 to-emerald-600/10', border: 'border-emerald-400/50', text: 'text-emerald-200', dot: 'bg-emerald-500' },
      { bg: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-400/50', text: 'text-amber-200', dot: 'bg-amber-500' },
      { bg: 'from-rose-500/20 to-rose-600/10', border: 'border-rose-400/50', text: 'text-rose-200', dot: 'bg-rose-500' },
    ];

    return flowColors[flowIndex % flowColors.length];
  }, [selectedFlowId, getTicketFlows]);

  // Flow görselleştirme component'i (eski büyük versiyon)
  const renderFlowVisualization = () => {
    const flows = getTicketFlows;
    const currentFlow = getCurrentFlow;
    if (flows.length === 0) {
      return (
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-slate-800/50 to-slate-900/50 px-6 py-8 shadow-lg">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="rounded-full bg-slate-700/50 p-4">
              <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-400">Flow bilgisi bulunamadı</p>
            <p className="text-xs text-slate-500">Ticket henüz herhangi bir flow'dan geçmemiş</p>
          </div>
        </div>
      );
    }

    const cacheEntry = detailTicket?.TICID ? eventCache[detailTicket.TICID] : null;
    const baseAllFields = cacheEntry?.allFields || currentTicketAllFields;
    const allEvents = Array.isArray(cacheEntry?.events) ? cacheEntry.events : [];

    const accentColors = [
      'border-l-blue-400', 'border-l-purple-400', 'border-l-emerald-400',
      'border-l-amber-400', 'border-l-rose-400',
    ];

    return (
      <div className="space-y-2">
        {flows.map((flow, index) => {
          const isCurrentFlow = flow.flowId === currentFlow;
          const accent = accentColors[index % accentColors.length];
          const flowEvents = allEvents.filter(
            (event) => (event.FLOWID || event.FLOW) === flow.flowId,
          );
          const flowAllFields =
            baseAllFields && typeof baseAllFields === 'object' && baseAllFields[flow.flowId]
              ? { [flow.flowId]: baseAllFields[flow.flowId] }
              : null;

          return (
            <div key={`${flow.flowId}-${index}`} className={`border-l-2 ${accent} bg-white/[0.03] rounded-r-lg`}>
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h4 className="text-[11px] font-semibold text-slate-100 truncate">
                    {flow.flowId}
                  </h4>
                  {isCurrentFlow && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium text-emerald-300 bg-emerald-500/15">
                      Aktif
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-shrink-0">
                  <span>{flow.firstSeenDate ? formatDate(flow.firstSeenDate) : ''}</span>
                  <span>{flow.eventCount} event</span>
                </div>
              </div>

              {flowAllFields && Object.keys(flowAllFields).length > 0 ? (
                <div className="px-3 pb-2">
                  <FormView formData={flowAllFields} flowEvents={flowEvents} />
                </div>
              ) : (
                <p className="px-3 pb-2 text-[10px] text-slate-500 text-center">
                  {flowEvents && flowEvents.length > 0
                    ? 'Form detayları bulunamadı'
                    : 'Detay bilgisi bulunamadı'}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Açılır/kapanır dropdown component helper
  const CollapsibleSection = ({ title, isOpen, onToggle, children, icon, noPadding = false, maxHeight = '70vh' }) => {
    return (
      <div className="rounded-xl border border-white/10 bg-gradient-to-br from-slate-800/50 to-slate-900/50 overflow-hidden shadow-lg flex flex-col">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors duration-150 group flex-shrink-0"
          type="button"
        >
          <div className="flex items-center gap-3">
            {icon && (
              <div className="text-slate-400 group-hover:text-slate-300 transition-colors">
                {icon}
              </div>
            )}
            <h3 className="text-sm font-semibold text-slate-200 group-hover:text-white transition-colors">
              {title}
            </h3>
          </div>
          <svg
            className={`h-5 w-5 text-slate-400 group-hover:text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          className={`overflow-hidden ${
            isOpen ? 'opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ transition: 'max-height 0.25s ease-in-out, opacity 0.2s ease' }}
        >
          <div 
            className={`overflow-y-auto collapsible-content ${noPadding ? '' : 'p-4 border-t border-white/10'}`} 
            style={{ 
              scrollbarWidth: 'thin',
              maxHeight: isOpen ? maxHeight : '0',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  };

  const renderDetailView = () => {
    
    return (
      <div className="flex flex-1 flex-col overflow-hidden bg-[#0f1727] text-slate-100">
        {/* Header - Ticket Özeti (Tek Satır, Sabit Yükseklik) */}
        <div className="flex-shrink-0 h-12 border-b border-white/10 bg-white/5 px-3">
          <div className="flex h-12 items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h3 className="text-sm font-semibold text-white whitespace-nowrap">
                #{detailTicket?.TICID || 'N/A'}
              </h3>

              {detailTicket && (
                <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold text-blue-200 border border-blue-400/15 whitespace-nowrap">
                  {detailTicket.STATUS?.value || detailTicket.STATUS || 'N/A'}
                </span>
              )}

              {isTicketVisited(detailTicket?.TICID) && (
                <HiEye className="h-3.5 w-3.5 text-blue-300/80 flex-shrink-0" title="Görüntülendi" />
              )}

              {/* Mainflow - tek satır, responsive */}
              {detailTicket && (
                <span className="hidden sm:inline text-[11px] text-slate-300/80 truncate min-w-0">
                  {detailTicket.MAINFLOW_TEXT || detailTicket.MAINFLOW || ''}
                </span>
              )}
            </div>

            <button
              onClick={handleReturnToList}
              className="rounded-lg border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-slate-100 transition hover:border-white/30 hover:bg-white/10 whitespace-nowrap flex-shrink-0"
            >
              ← Geri
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto detail-view-scroll" style={{ scrollbarWidth: 'thin' }}>
            {detailTicket && getTicketFlows.length > 0 ? (
              renderFlowVisualization()
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="rounded-full bg-slate-700/50 p-4">
                  <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-400">Flow bilgisi bulunamadı</p>
                <p className="text-xs text-slate-500">Ticket henüz herhangi bir flow'dan geçmemiş</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="flex h-full flex-col bg-[#1A283F] text-slate-200">
      {isDetailView && detailTicket ? renderDetailView() : renderListView()}

      {isModalOpen && (
        <EditTicketModal ticket={ticketToEdit} onClose={() => setIsModalOpen(false)} />
      )}
    </section>
  );
}

export default LeftPanelTickets;
